import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { apiError } from '../../services/api';
import { useApi } from '../../hooks/useApi';
import StatusPill from '../../components/StatusPill';
import { Card, EmptyState, LoadingRows, Notice, PageHeader, StatCard, Toggle } from '../../components/ui/Page';

export type AiModel = {
  id: string;
  provider: 'GEMINI';
  label: string;
  model: string;
  description: string | null;
  strengths: string | null;
  isDefault: boolean;
  isActive: boolean;
  sortOrder: number;
  _count: { prompts: number };
};

type Status = { apiKeyConfigured: boolean; envModelOverride: string | null; defaultModel: string };
type TestResult = { ok: boolean; latencyMs: number; reply?: string; error?: string };

export default function AiSettingsPage() {
  const navigate = useNavigate();
  const status = useApi<Status>('/admin/ai/status');
  const { data, loading, error, reload } = useApi<{ providers: AiModel[] }>('/admin/ai/providers');
  const [busy, setBusy] = useState<string | null>(null);
  const [tests, setTests] = useState<Record<string, TestResult | 'running'>>({});
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const models = data?.providers || [];
  const def = models.find((m) => m.isDefault);

  const patch = async (m: AiModel, body: Partial<Pick<AiModel, 'isActive' | 'isDefault'>>, done: string) => {
    setBusy(m.id);
    setNotice(null);
    try {
      await api.patch(`/admin/ai/providers/${m.id}`, body);
      setNotice({ tone: 'success', text: done });
      await reload();
    } catch (e) {
      setNotice({ tone: 'error', text: apiError(e, 'Update failed.') });
    } finally {
      setBusy(null);
    }
  };

  const runTest = async (m: AiModel) => {
    setTests((t) => ({ ...t, [m.id]: 'running' }));
    try {
      const { data: res } = await api.post<TestResult>(`/admin/ai/providers/${m.id}/test`);
      setTests((t) => ({ ...t, [m.id]: res }));
    } catch (e) {
      setTests((t) => ({ ...t, [m.id]: { ok: false, latencyMs: 0, error: apiError(e, 'Test failed.') } }));
    }
  };

  const s = status.data;

  return (
    <div>
      <PageHeader
        title="AI Settings"
        description="Model catalog for the Micus AI platform. Ratings, verification and copilot resolve a model from here — not by calling a vendor SDK from the workspace. Usage is in Support."
        actions={
          <Link to="/ai-settings/new" className="mz-btn-primary">
            + Add model
          </Link>
        }
      />

      {s && !s.apiKeyConfigured && (
        <Notice tone="warning" className="mb-4">
          <strong>AI_API_KEY / GEMINI_API_KEY is not set</strong> on the API server. Ratings and document checks will fail until it is configured.
        </Notice>
      )}
      {s?.envModelOverride && (
        <Notice tone="info" className="mb-4">
          The server's <code className="font-mono">AI_MODEL</code> / <code className="font-mono">GEMINI_MODEL</code> override is set to <strong>{s.envModelOverride}</strong> and is used when no catalog default is set.
        </Notice>
      )}
      {(notice || error) && (
        <Notice tone={notice?.tone || 'error'} onClose={() => setNotice(null)} className="mb-4">
          {notice?.text || error}
        </Notice>
      )}

      <div className="mz-fade-up grid grid-cols-2 gap-3 md:grid-cols-3">
        <StatCard label="Platform" value="Micus AI" hint={s ? (s.apiKeyConfigured ? 'API key configured' : 'API key missing') : '…'} />
        <StatCard label="Default model" value={<span className="text-base">{def?.label || '—'}</span>} hint={def?.model} accent />
        <StatCard label="Active models" value={models.filter((m) => m.isActive).length} hint={`of ${models.length} configured`} />
      </div>

      <Card className="mz-fade-up mz-d1 mt-4" padded={false} title="Models" subtitle="Only active models can be picked in Ratings.">
        {loading && !data ? (
          <LoadingRows />
        ) : models.length === 0 ? (
          <EmptyState
            title="No models in the catalog yet"
            description="Add a model to start rating companies."
            action={
              <Link to="/ai-settings/new" className="mz-btn-primary">
                + Add model
              </Link>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="mz-table min-w-[720px]">
              <thead>
                <tr>
                  <th>Model</th>
                  <th>Used by</th>
                  <th>Connection</th>
                  <th className="text-center">Active</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {models.map((m) => {
                  const t = tests[m.id];
                  return (
                    <tr key={m.id} className={`mz-row-link ${m.isActive ? '' : 'opacity-55'}`} onClick={() => navigate(`/ai-settings/${m.id}`)}>
                      <td className="max-w-[340px]">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{m.label}</span>
                          {m.isDefault && <StatusPill label="Default" />}
                        </div>
                        <p className="mt-0.5 font-mono text-[11px] text-[#b77c98]">{m.model}</p>
                        {m.strengths && <p className="mt-0.5 line-clamp-1 text-xs text-[#a77d92]">{m.strengths}</p>}
                      </td>
                      <td className="text-xs text-[#9a6b82]">
                        {m._count.prompts ? `${m._count.prompts} rating prompt${m._count.prompts === 1 ? '' : 's'}` : m.isDefault ? 'All defaults' : '—'}
                      </td>
                      <td className="max-w-[220px] text-xs" onClick={(e) => e.stopPropagation()}>
                        {t === 'running' ? (
                          <span className="text-[#a77d92]">Testing…</span>
                        ) : t ? (
                          <span title={t.reply || t.error} className={t.ok ? 'font-semibold text-[#047857]' : 'line-clamp-2 text-[#be123c]'}>
                            {t.ok ? `✓ Online · ${(t.latencyMs / 1000).toFixed(1)}s` : `✕ ${t.error}`}
                          </span>
                        ) : (
                          <button type="button" className="font-semibold text-[#d6006e] hover:underline" onClick={() => void runTest(m)}>
                            Test connection
                          </button>
                        )}
                      </td>
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        <Toggle
                          size="sm"
                          checked={m.isActive}
                          disabled={busy === m.id || m.isDefault}
                          onChange={(v) => void patch(m, { isActive: v }, `${m.label} ${v ? 'enabled' : 'disabled'}.`)}
                          label="Active"
                        />
                      </td>
                      <td className="text-right" onClick={(e) => e.stopPropagation()}>
                        {!m.isDefault && (
                          <button
                            type="button"
                            disabled={busy === m.id}
                            className="whitespace-nowrap text-xs font-semibold text-[#d6006e] hover:underline disabled:opacity-50"
                            onClick={() => void patch(m, { isDefault: true }, `${m.label} is now the default model.`)}
                          >
                            Make default
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
